import React from 'react'
import { FormattedMessage } from 'react-intl'
import { gnarSpec } from '../../../lib/tracking/gnar'
import { Button, ButtonStyles, Typography, FixedSpacer } from '@grammarly/badger-ui'
import { withPublicAssetUrl } from '../../../lib/utils/cdn'
import { getConfig } from '../../../config'
import styles from './citation-generator.module.scss'
import cn from 'classnames'

export const ctaInPreviewTargetUrl = 'https://www.grammarly.com/signup?flow_source=citation_generator'

const gradStack = `${withPublicAssetUrl('citation-generator/grad-stack.svg')}`
const autoCite = `${withPublicAssetUrl('citation-generator/auto-citation.png')}`
const Checkmark = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 12.8894L10.0769 16.0005L17 9.00049" stroke="#11A683" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

export interface GetGrammarlyCtaCardContentProps {
  pageId: string
  slug: string
}

export const GetGrammarlyCtaCardContent: React.FC<GetGrammarlyCtaCardContentProps> = ({ pageId, slug }) => {
  const baseUrl = getConfig().urls.main

  return (
    <div>
      <img src={gradStack} alt="" className={styles.getGrammarlyCtaCardImage} height="110px" />
      <FixedSpacer multiplier={3} />
      <Typography.C1 as="h2">
        <FormattedMessage
          id="citationGenerator.getGrammarlyCtaCardContent.heading"
          description="Citation Generator - Get Grammarly CTA Card Content - Heading"
          defaultMessage="A Better Way to Work"
        />
      </Typography.C1>
      <Typography.C5 as="p" className={styles.getGrammarlyCtaCardText}>
        <FormattedMessage
          id="citationGenerator.getGrammarlyCtaCardContent.copy"
          description="Citation Generator - Get Grammarly CTA Card Content - Copy"
          defaultMessage="Feel confident you've done your best work with the help of Grammarly. Benefit from:"
        />
      </Typography.C5>
      <ul className={styles.getGrammarlyCtaCardList}>
        <li>
          <div className={styles.getGrammarlyCtaCardListElement}>
            <Checkmark />
            <a
              href={`${baseUrl}/grammarlygo`}
              target="_blank"
              rel="noreferrer"
              className={styles.getGrammarlyCtaCardListFeature}
            >
              <Typography.C4 as="p">
                <FormattedMessage
                  id="citationGenerator.getGrammarlyCtaCardContent.list.item1"
                  description="Citation Generator - Get Grammarly CTA Card Content - List - Item 1"
                  defaultMessage="Generative AI assistance"
                />
              </Typography.C4>
            </a>
          </div>
        </li>
        <li>
          <div className={styles.getGrammarlyCtaCardListElement}>
            <Checkmark />
            <a
              href={`${baseUrl}/a/citations`}
              target="_blank"
              rel="noreferrer"
              className={styles.getGrammarlyCtaCardListFeature}
            >
              <Typography.C4 as="p">
                <FormattedMessage
                  id="citationGenerator.getGrammarlyCtaCardContent.list.item2"
                  description="Citation Generator - Get Grammarly CTA Card Content - List - Item 2"
                  defaultMessage="Automatically created citations"
                />
              </Typography.C4>
            </a>
          </div>
        </li>
        <li>
          <div className={styles.getGrammarlyCtaCardListElement}>
            <Checkmark />
            <a
              href={`${baseUrl}/tone`}
              target="_blank"
              rel="noreferrer"
              className={styles.getGrammarlyCtaCardListFeature}
            >
              <Typography.C4 as="p">
                <FormattedMessage
                  id="citationGenerator.getGrammarlyCtaCardContent.list.item3"
                  description="Citation Generator - Get Grammarly CTA Card Content - List - Item 3"
                  defaultMessage="Tone and clarity suggestions"
                />
              </Typography.C4>
            </a>
          </div>
        </li>
        <li>
          <div className={styles.getGrammarlyCtaCardListElement}>
            <Checkmark />
            <a
              href={`${baseUrl}/plans`}
              target="_blank"
              rel="noreferrer"
              className={styles.getGrammarlyCtaCardListFeature}
            >
              <Typography.C4 as="p">
                <FormattedMessage
                  id="citationGenerator.getGrammarlyCtaCardContent.list.item4"
                  description="Citation Generator - Get Grammarly CTA Card Content - List - Item 4"
                  defaultMessage="And more"
                />
              </Typography.C4>
            </a>
          </div>
        </li>
      </ul>
      <FixedSpacer multiplier={3} />
      <Button
        className={cn(ButtonStyles.WhiteButtonBlueTextWithBorder, styles.getGrammarlyCtaCardButton)}
        padding="compact"
        as="a"
        onClick={() => {
          gnarSpec.customLinkCtaButtonClick(
            'CitationGenerator_PersistentCard_SignUp',
            pageId,
            slug,
            ctaInPreviewTargetUrl,
            'Section',
            'CitationGenerator_SignUpWall'
          )
        }}
        href={ctaInPreviewTargetUrl}
        target="_blank"
      >
        <span>
          <FormattedMessage id="phrase.getGrammarly" description="Get Grammarly" defaultMessage="Get Grammarly" />
          &nbsp;
          <FormattedMessage id="phrase.itsFree" description="Sign up prompt value prop" defaultMessage="It's free" />
        </span>
      </Button>
    </div>
  )
}

export interface GetGrammarlyModalContentProps {
  modalTitle: string
  modalBody: string
  gnarCtaClickEventName:
    | 'CitationGenerator_CopyCitationClickModal_SignUp'
    | 'CitationGenerator_SecondPageShowModal_SignUp'
  pageId: string
  slug: string
}

export const GetGrammarlyModalContent: React.FC<GetGrammarlyModalContentProps> = ({
  modalTitle,
  modalBody,
  gnarCtaClickEventName,
  pageId,
  slug,
}) => {
  return (
    <div className={styles.modalContent}>
      <img src={autoCite} alt="" height="304px" />
      <div className={styles.modalText}>
        <Typography.T3 as="h2">{modalTitle}</Typography.T3>
        <FixedSpacer multiplier={2} />
        <Typography.C4 as="p" className={styles.modalBody}>
          {modalBody}
        </Typography.C4>
        <FixedSpacer multiplier={2} />
        <Button
          className={cn(ButtonStyles.GreenButtonWhiteText, styles.modalButtonStyle)}
          padding="cta"
          as="a"
          onClick={() => {
            gnarSpec.customLinkCtaButtonClick(
              gnarCtaClickEventName,
              pageId,
              slug,
              ctaInPreviewTargetUrl,
              'Section',
              'CitationGenerator_SignUpWall'
            )
          }}
          href={ctaInPreviewTargetUrl}
          target="_blank"
        >
          <span className={styles.buttonText}>
            <FormattedMessage id="phrase.getGrammarly" description="Get Grammarly" defaultMessage="Get Grammarly" />
          </span>
          &nbsp;
          <FormattedMessage id="phrase.itsFree" description="Sign up prompt value prop" defaultMessage="It's free" />
        </Button>
      </div>
    </div>
  )
}
